import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getDynamicSections, deleteDynamicSection } from "../../../redux/home/action";
import { Plus, Pencil, Trash2 } from "lucide-react";
import swal from "sweetalert";
import "./HomePageCRM.css";
import { usePermissions } from "../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../lib/permissions";
import HomePageTabs from "../Banners/HomePageTabs";

const DynamicSectionsList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const sectionsData = useSelector((state) => state.reusableSlice?.dynamicSections);
  const { hasAction } = usePermissions();

  const [searchKey, setSearchKey] = useState("");
  const [surfaceFilter, setSurfaceFilter] = useState("");
  const [page, setPage] = useState(1);
  const [deleteLoading, setDeleteLoading] = useState(null);

  const sections = [...(sectionsData?.data || [])].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0)
  );
  const pagination = sectionsData?.pagination || null;

  const editable = hasAction(MENU.HOME, PERMISSION.EDIT);

  const fetchSections = useCallback(() => {
    // Trust-strip claims share this table but are not sections — without the
    // filter they land in this list, where every column that matters
    // (content, picks, display) is blank and Edit opens the wrong form.
    const params = { page, per_page: 10, section_type: "product_section" };
    if (searchKey) params.search_key = searchKey;
    if (surfaceFilter) params.surface = surfaceFilter;
    dispatch(getDynamicSections(params));
  }, [dispatch, page, searchKey, surfaceFilter]);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  useEffect(() => {
    setPage(1);
  }, [searchKey, surfaceFilter]);

  const handleDelete = (section) => {
    swal({
      title: "Delete Section",
      text: `Are you sure you want to delete "${section.name}"?`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then((confirm) => {
      if (confirm) {
        dispatch(
          deleteDynamicSection(
            section.id,
            (val) => setDeleteLoading(val ? section.id : null),
            fetchSections
          )
        );
      }
    });
  };

  const totalPages = pagination?.pages || 1;

  return (
    <div className="ds-container">
      <div className="ds-header">
        <div>
          <h4>Home Page</h4>
          <p>Product sections that appear between banner zones on the homepage.</p>
        </div>
        {editable && (
          <button className="ds-btn-primary" onClick={() => navigate("/dynamic-sections/create")}>
            <Plus size={16} /> New Section
          </button>
        )}
      </div>

      <div style={{ marginBottom: 16 }}>
        <HomePageTabs />
      </div>

      <div className="ds-filters">
        <input
          type="text"
          placeholder="Search by name..."
          value={searchKey}
          onChange={(e) => setSearchKey(e.target.value)}
          style={{ width: 220 }}
        />
        <select value={surfaceFilter} onChange={(e) => setSurfaceFilter(e.target.value)}>
          <option value="">All Surfaces</option>
          <option value="app">App</option>
          <option value="web">Web</option>
          <option value="both">Both</option>
        </select>
      </div>

      <div className="ds-table-wrapper">
        <table className="ds-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Content</th>
              <th>Surface</th>
              <th>Pos</th>
              <th>Status</th>
              {/* Schedule column removed with the start/end date fields — a
                  section is on or off via Status. */}
              {editable && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {sections.length === 0 ? (
              <tr>
                <td colSpan={editable ? 7 : 6} style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
                  No sections found. {editable ? "Create your first section." : ""}
                </td>
              </tr>
            ) : (
              sections.map((s, i) => (
                <tr key={s.id}>
                  <td>{(page - 1) * 10 + i + 1}</td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{s.name}</div>
                    {s.ar_name && (
                      <div style={{ fontSize: 11, color: "#9ca3af", direction: "rtl" }}>{s.ar_name}</div>
                    )}
                  </td>
                  <td>
                    {s.content_type ? (
                      <span className="ds-badge ds-badge--content">{s.content_type}</span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <span className="ds-badge ds-badge--surface">{s.surface}</span>
                  </td>
                  <td>{s.position}</td>
                  <td>
                    <span className={`ds-badge ${s.is_active ? "ds-badge--active" : "ds-badge--inactive"}`}>
                      {s.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  {editable && (
                    <td>
                      <div className="ds-actions">
                        <button
                          className="ds-btn-edit"
                          onClick={() => navigate(`/dynamic-sections/edit/${s.id}`)}
                        >
                          <Pencil size={12} />
                        </button>
                        <button
                          className="ds-btn-delete"
                          disabled={deleteLoading === s.id}
                          onClick={() => handleDelete(s)}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>

        {pagination && pagination.pages > 1 && (
          <div className="ds-pagination">
            <span>
              Page {page} of {totalPages} ({pagination.total} sections)
            </span>
            <div className="ds-pagination-buttons">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)}>
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => Math.abs(p - page) <= 2 || p === 1 || p === totalPages)
                .map((p, idx, arr) => (
                  <React.Fragment key={p}>
                    {idx > 0 && arr[idx - 1] !== p - 1 && (
                      <span style={{ padding: "0 4px" }}>...</span>
                    )}
                    <button
                      className={page === p ? "active" : ""}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                ))}
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicSectionsList;
